<template>
  <div class="custom-hints-grid" :style="{ height: mHeight ? mHeight + 'px' : '100%' }">
    <div v-if="loading" class="custom-hints-grid__loading">Загрузка...</div>
    <div v-else-if="!hintsData.length" class="custom-hints-grid__empty">Нет подсказок</div>
    <div v-else class="custom-hints-grid__content"
      :style="{ 'grid-template-columns': `repeat(${titles.length}, minmax(0, 1fr))` }">
      <!-- Headers -->
      <div v-for="title in titles" :key="'h-'+title.Id"
        class="custom-hints-grid__header">{{ title.Name }}</div>

      <!-- Rows -->
      <template v-for="(row, rowIdx) in hintsData" :key="'r-'+rowIdx">
        <!-- Main value cell -->
        <div class="custom-hints-grid__cell custom-hints-grid__cell--main"
          @click="selectValWithHinted(row)">
          {{ row.HintedValue }} ({{ row.UsageCount }})
        </div>

        <!-- Note cells -->
        <template v-for="(note, noteIdx) in row.Notes" :key="'n-'+rowIdx+'-'+noteIdx">
          <div v-if="note" class="custom-hints-grid__cell custom-hints-grid__cell--note">
            <mosta-checkbox v-if="note.CanBeInsert"
              :model-value="noteHasSelection(note)"
              @update:model-value="toggleNoteCheckbox(row, note)" />
            <span class="custom-hints-grid__note-name">
              {{ (note.SelectedHint || note.Hints[0])?.NameBack || '—' }}
            </span>
            <a v-if="note.Hints.length > 1" class="custom-hints-grid__all-link"
              :ref="el => setNoteAnchor(rowIdx, noteIdx, el)"
              @click.stop="showNoteHints(rowIdx, noteIdx, note)">
              (Все)
            </a>
          </div>
          <div v-else class="custom-hints-grid__cell custom-hints-grid__cell--empty"></div>
        </template>
      </template>
    </div>

    <!-- Note hints popup (plain div, not q-menu, to stay inside .fmenu DOM tree) -->
    <div v-if="notePopupOpen" ref="notePopupEl" tabindex="-1"
      class="custom-hints-grid__popup" :style="popupStyle"
      @mousedown.stop @click.stop @keydown.stop
      @keydown.esc="closePopup">
      <div class="custom-hints-grid__popup-inner">
        <div class="custom-hints-grid__popup-header">
          <input v-model="noteSearchText" class="custom-hints-grid__popup-search-input"
            placeholder="Поиск..." @input="noteSearchText = ($event.target as HTMLInputElement).value" />
          <span class="custom-hints-grid__popup-close" @click="closePopup">&times;</span>
        </div>
        <div class="custom-hints-grid__popup-list">
          <div v-for="hint in filteredNoteHints" :key="hint.Id"
            class="custom-hints-grid__popup-item"
            :class="{ 'custom-hints-grid__popup-item--selected': hint.Selected }"
            @click="selectValHint(hint)">
            <mosta-checkbox :model-value="hint.Selected"
              @update:model-value="selectValHint(hint)" />
            <span>{{ hint.NameBack }} ({{ hint.Count }})</span>
          </div>
          <div v-if="!filteredNoteHints.length" class="custom-hints-grid__popup-empty">
            Нет значений
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-facing-decorator';
import MostaCheckbox from '@/components/common/basic/MostaCheckbox.vue';
import type { ParameterHintView, ParameterHintNoteView, HintView } from '@/models/interfaces/storage/storage-models';

/**
 * Кастомные подсказки для позиций склада (storageContainer/custom).
 *
 * Отображает таблицу (CSS Grid), где:
 *  - 1-я колонка — основное подсказываемое значение (HintedValue) + кол-во использований
 *  - Остальные колонки — доп. наборы (Notes), сгруппированные по ValueSetId
 *
 * Пример таблицы (titles = [Позиция, Цена, Комментарий]):
 *
 *   | Позиция           | Цена          | Комментарий      |
 *   |--------------------|---------------|------------------|
 *   | Кабель ВВГ (12)    | [x] 500 (Все) | [ ] текст (Все)  |
 *   | Кабель КГ  (8)     | [ ] 300 (Все) | [x] заметка      |
 *
 * Чекбоксы (CanBeInsert):
 *   Если ValueSetId данного note-столбца есть в possibleValueSetsIds, чекбокс показывается.
 *   Там лежат наборы смежных одиночных полей того уровня записи, где стоит активное поле,
 *   без набора самого поля (см. possibleValueSetsIds в ParameterInline).
 *   Чекбокс позволяет заранее выбрать значение из note, которое при клике на строку
 *   автоматически заполнит смежное поле с этим набором. Подстановка ищет шире, чем
 *   показывается галка: она доходит и до элементов списка, и до вложенных записей.
 *   Без чекбокса note — просто информация, при выборе строки она игнорируется.
 *
 * Поведение выбора:
 *   1. Чекбокс/radio в popup (selectValHint): внутри одного note-столбца можно выбрать
 *      только одно значение (radio). При этом в ДРУГИХ строках таблицы note с тем же
 *      ValueSetId сбрасывается (кросс-строчная эксклюзивность).
 *   2. Клик по строке (selectValWithHinted): основное значение берётся из неё.
 *      Отмеченные галки собираются по всей таблице, не только из этой строки.
 *      Основное значение попадёт в defhints, а notes заполнят смежные поля по ValueSet.Id.
 *   3. "(Все)" — popup со всеми вариантами note, поиск, выбор чекбоксом.
 */

@Component({
  name: 'CustomHintsGrid',
  components: { MostaCheckbox },
  emits: ['hint-selected'],
})
export default class CustomHintsGrid extends Vue {
  @Prop({ type: Array, default: () => [] }) hintsData!: ParameterHintView[];
  @Prop({ type: Array, default: () => [] }) titles!: { Id: number; Name: string }[];
  @Prop({ type: Boolean, default: false }) loading!: boolean;
  @Prop({ type: Number, default: 0 }) mHeight!: number;

  notePopupOpen = false;
  notePopupAnchor: HTMLElement | null = null;
  noteSearchText = '';
  activePopupRowIdx = -1;
  activePopupNoteIdx = -1;
  popupTop = 0;
  popupLeft = 0;
  popupOpenUp = false;

  private noteAnchors = new Map<string, HTMLElement>();
  private outsideClickHandler: ((e: MouseEvent) => void) | null = null;

  get popupStyle(): Record<string, string> {
    const style: Record<string, string> = {
      right: (window.innerWidth - this.popupLeft) + 'px',
    };
    if (this.popupOpenUp) {
      style.bottom = (window.innerHeight - this.popupTop) + 'px';
    } else {
      style.top = this.popupTop + 'px';
    }
    return style;
  }

  setNoteAnchor(rowIdx: number, noteIdx: number, el: any) {
    if (el) {
      this.noteAnchors.set(`${rowIdx}-${noteIdx}`, el instanceof HTMLElement ? el : el.$el);
    }
  }

  noteHasSelection(note: ParameterHintNoteView): boolean {
    return note.SelectedHint !== null;
  }

  toggleNoteCheckbox(row: ParameterHintView, note: ParameterHintNoteView) {
    if (note.SelectedHint) {
      note.SelectedHint.Selected = false;
      note.SelectedHint = null;
    } else if (note.Hints.length > 0) {
      this.doSelectHintInNote(row, note, note.Hints[0]);
    }
  }

  get activeNote(): ParameterHintNoteView | null {
    if (this.activePopupRowIdx < 0 || this.activePopupNoteIdx < 0) return null;
    const row = this.hintsData[this.activePopupRowIdx];
    if (!row) return null;
    return row.Notes[this.activePopupNoteIdx] ?? null;
  }

  get filteredNoteHints(): HintView[] {
    const note = this.activeNote;
    if (!note) return [];
    const search = (this.noteSearchText || '').toLowerCase();
    return note.Hints.filter(h => !search || h.NameBack.toLowerCase().includes(search));
  }

  showNoteHints(rowIdx: number, noteIdx: number, note: ParameterHintNoteView) {
    this.activePopupRowIdx = rowIdx;
    this.activePopupNoteIdx = noteIdx;
    this.noteSearchText = '';
    const anchor = this.noteAnchors.get(`${rowIdx}-${noteIdx}`);
    if (anchor) {
      this.notePopupAnchor = anchor;
      const anchorRect = anchor.getBoundingClientRect();
      const spaceBelow = window.innerHeight - anchorRect.bottom;
      const spaceAbove = anchorRect.top;
      this.popupOpenUp = spaceAbove > spaceBelow;
      this.popupTop = this.popupOpenUp ? anchorRect.top : anchorRect.bottom;
      this.popupLeft = anchorRect.right;
      this.notePopupOpen = true;
      this.$nextTick(() => {
        (this.$refs.notePopupEl as HTMLElement)?.focus();
      });
      this.attachOutsideHandlers();
    }
  }

  closePopup() {
    this.notePopupOpen = false;
    this.detachOutsideHandlers();
  }

  private outsideKeyHandler: ((e: KeyboardEvent) => void) | null = null;

  private attachOutsideHandlers() {
    this.detachOutsideHandlers();
    this.outsideClickHandler = (e: MouseEvent) => {
      const popupEl = this.$refs.notePopupEl as HTMLElement | undefined;
      if (popupEl && !popupEl.contains(e.target as Node)) {
        e.stopPropagation();
        e.preventDefault();
        this.closePopup();
      }
    };
    this.outsideKeyHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        e.preventDefault();
        this.closePopup();
      }
    };
    setTimeout(() => {
      document.addEventListener('mousedown', this.outsideClickHandler!, true);
      document.addEventListener('keydown', this.outsideKeyHandler!, true);
    }, 0);
  }

  private detachOutsideHandlers() {
    if (this.outsideClickHandler) {
      document.removeEventListener('mousedown', this.outsideClickHandler!, true);
      this.outsideClickHandler = null;
    }
    if (this.outsideKeyHandler) {
      document.removeEventListener('keydown', this.outsideKeyHandler!, true);
      this.outsideKeyHandler = null;
    }
  }

  beforeUnmount() {
    this.detachOutsideHandlers();
  }

  selectValHint(hint: HintView) {
    const note = this.activeNote;
    const row = this.hintsData[this.activePopupRowIdx];
    if (!note || !row) return;
    this.doSelectHintInNote(row, note, hint);
  }

  private doSelectHintInNote(row: ParameterHintView, note: ParameterHintNoteView, hint: HintView) {
    // Radio within note: deselect all others
    note.Hints.forEach(h => { h.Selected = false; });
    hint.Selected = true;
    note.SelectedHint = hint;

    // Move selected to front
    const idx = note.Hints.indexOf(hint);
    if (idx > 0) {
      note.Hints.splice(idx, 1);
      note.Hints.unshift(hint);
    }

    // Cross-row exclusivity: deselect same ValueSetId in other rows
    for (const otherRow of this.hintsData) {
      if (otherRow === row) continue;
      for (const otherNote of otherRow.Notes) {
        if (otherNote && otherNote.ValueSetId === note.ValueSetId) {
          otherNote.Hints.forEach(h => { h.Selected = false; });
          otherNote.SelectedHint = null;
        }
      }
    }
  }

  selectValWithHinted(row: ParameterHintView) {
    const value = { Id: row.HintedValueId, Name: row.HintedValue };

    const notes: [number, { Id: number; Name: string }][] = [];
    const seen = new Set<number>();
    const pushChecked = (note: ParameterHintNoteView | null) => {
      if (!note || !note.CanBeInsert || !note.SelectedHint) return;
      if (seen.has(note.ValueSetId)) return;
      seen.add(note.ValueSetId);
      notes.push([
        note.ValueSetId,
        { Id: note.SelectedHint.Id, Name: note.SelectedHint.NameBack },
      ]);
    };

    // Сначала строка, которую выбрали: при одном наборе она важнее остальных.
    for (const note of row.Notes) pushChecked(note);
    for (const other of this.hintsData) {
      if (other === row) continue;
      for (const note of other.Notes) pushChecked(note);
    }

    this.$emit('hint-selected', { value, notes });
  }
}
</script>

<style lang="scss" scoped>
/* Гарнитура перечисляется вместе с потомками, потому что глобальное правило в app.scss
   задаёт её через универсальный селектор каждому элементу напрямую, и наследование от
   контейнера до ячеек не доходит. Кегль не задаём: он остаётся общим, как в подсказках
   по умолчанию. */
.custom-hints-grid,
.custom-hints-grid * {
  font-family: Roboto, "Helvetica Neue", Arial, "Segoe UI", sans-serif;
}

.custom-hints-grid {
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
  width: 100%;

  &__loading,
  &__empty {
    text-align: center;
    padding: 20px;
    color: #999;
  }

  &__content {
    display: grid;
    width: 100%;
  }

  &__header {
    color: #979797;
    font-weight: 500;
    padding: 6px 8px;
    min-width: 0;
    overflow-wrap: anywhere;
    border-bottom: 1px solid #ddd;
    /* Шапка липкая, поэтому под текстом нужна непрозрачная поверхность в цвет меню:
       без неё строки при прокрутке просвечивают сквозь заголовок. */
    background: #fff;
    position: sticky;
    top: 0;
    z-index: 1;
  }

  &__cell {
    padding: 4px 8px;
    border-bottom: 1px solid #eee;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px;
    min-height: 28px;
    min-width: 0;
    overflow-wrap: anywhere;

    &--main {
      font-weight: 500;
      cursor: pointer;
      &:hover {
        background: #f0f7ff;
      }
    }

    &--empty {
    }
  }

  &__note-name {
    white-space: normal;
    overflow-wrap: anywhere;
    min-width: 0;
  }

  &__all-link {
    color: #499259;
    cursor: pointer;
    white-space: nowrap;
    &:hover {
      text-decoration: underline;
    }
  }

  &__popup {
    position: fixed;
    z-index: 9999;
    background: #fff;
    border: 1px solid #ccc;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  }

  &__popup-inner {
    min-width: 200px;
    max-height: 300px;
    display: flex;
    flex-direction: column;
  }

  &__popup-header {
    display: flex;
    align-items: center;
    border-bottom: 1px solid #eee;
  }

  &__popup-search-input {
    flex: 1;
    border: none;
    outline: none;
    padding: 6px 8px;
    background: transparent;
  }

  &__popup-close {
    padding: 4px 8px;
    cursor: pointer;
    font-size: 16px;
    color: #999;
    &:hover { color: #333; }
  }

  &__popup-list {
    overflow-y: auto;
    flex: 1;
  }

  &__popup-item {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px;
    cursor: pointer;

    &:hover {
      background: #f0f7ff;
    }

    &--selected {
      background: #e3f2fd;
    }
  }

  &__popup-empty {
    padding: 12px;
    text-align: center;
    color: #999;
  }
}
</style>
